import { Inject, Injectable, OnDestroy, signal } from '@angular/core'
import { MsalBroadcastService, MsalService } from '@azure/msal-angular'
import {
  AccountInfo,
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionStatus,
  IdTokenClaims,
} from '@azure/msal-browser'
import { Subject, filter, takeUntil } from 'rxjs'
import { APP_CONFIG } from 'src/app/app.config'
import { AppConfigModel } from '../models'
import { DefaultAadPermissions, Permissions } from '../enums/permissions.enum'
import { WarningDatasources } from 'src/app/shared/enums/warning-types.enum'

type IdTokenClaimsWithPolicyId = IdTokenClaims & { acr?: string; tfp?: string }

@Injectable({ providedIn: 'root' })
export class AuthService implements OnDestroy {
  _destroying$ = new Subject<void>()
  loginDisplay = false
  private readonly _activeAccount = signal<AccountInfo | null>(null)
  public readonly activeAccount = this._activeAccount.asReadonly()
  public activeAccountChanges = new Subject<AccountInfo>()
  private MsalInitialized: boolean = false

  constructor(
    private readonly msalService: MsalService,
    private readonly msalBroadcastService: MsalBroadcastService,
    @Inject(APP_CONFIG) private readonly config: AppConfigModel,
  ) {
    this._activeAccount.set(this.getAccountInfo())

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_SUCCESS ||
            msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS,
        ),
        takeUntil(this._destroying$),
      )
      .subscribe(async (result: EventMessage) => {
        let payload = result.payload as AuthenticationResult
        let idtoken = payload.idTokenClaims as IdTokenClaimsWithPolicyId

        if (
          idtoken.acr === this.config.aadb2c_policy ||
          idtoken.tfp === this.config.aadb2c_policy
        ) {
          this.msalService.instance.setActiveAccount(payload.account)
          if (payload.account) {
            this._activeAccount.set(payload.account)
            this.activeAccountChanges.next(payload.account)
          }
        }
        return result
      })

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE),
        takeUntil(this._destroying$),
      )
      .subscribe(async (result: EventMessage) => {
        const accounts = this.safeGetAllAccounts()
        const fallbackAccount = this.safeGetActiveAccount() ?? accounts[0] ?? null

        if (fallbackAccount) {
          if (!this.safeGetActiveAccount()) {
            this.msalService.instance.setActiveAccount(fallbackAccount)
          }
          this._activeAccount.set(fallbackAccount)
          this.activeAccountChanges.next(fallbackAccount)
          return
        }

        this._activeAccount.set(null)
      })

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$),
      )
      .subscribe((value) => {
        this.setLoginDisplay()
        this.checkAndSetActiveAccount()
      })
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined)
    this._destroying$.complete()
  }

  public msalIsInitialized(): boolean {
    return this.MsalInitialized
  }

  private safeGetAllAccounts(): AccountInfo[] {
    try {
      return this.msalService.instance.getAllAccounts()
    } catch {
      return []
    }
  }

  private safeGetActiveAccount(): AccountInfo | null {
    try {
      return this.msalService.instance.getActiveAccount()
    } catch {
      return null
    }
  }

  private async setLoginDisplay() {
    this.loginDisplay = this.safeGetAllAccounts().length > 0
  }

  private async checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    if (!this.MsalInitialized) {
      return
    }

    const accounts = this.safeGetAllAccounts()
    let activeAccount = this.safeGetActiveAccount()

    if (!activeAccount && accounts.length > 0) {
      activeAccount = accounts[0]
      this.msalService.instance.setActiveAccount(activeAccount)
      this.activeAccountChanges.next(activeAccount)
    }

    this._activeAccount.set(activeAccount ?? null)

    if (!activeAccount) {
      return
    }

    try {
      await this.msalService.instance.acquireTokenSilent({
        scopes: [this.config.scope],
        account: activeAccount,
      })
    } catch {
      // Keep the restored account state even if token refresh fails transiently.
    }
  }

  public logout(): void {
    this.msalService.logoutRedirect()
    this._activeAccount.set(null)
    this.MsalInitialized = false
  }

  public getAccountInfo(): AccountInfo | null {
    return this.safeGetActiveAccount()
  }

  public getUserClaims(): {
    userPermissions: Permissions[]
    userDataFilters: WarningDatasources[]
    userBackendScopes: string[]
  } {
    let userPermissions: Permissions[] = []
    let userDataFilters: WarningDatasources[] = []
    let userBackendScopes: string[] = []

    const activeAccount = this.safeGetActiveAccount()

    let tokenPermissions = (activeAccount?.idTokenClaims?.[`permissions`] as Permissions[]) || []

    let tokenDataFilters = (
      (activeAccount?.idTokenClaims?.[`dataFilters`] as WarningDatasources[]) || []
    ).filter((dataFilter) => dataFilter.startsWith('warnings'))

    let tokenBackendScopes = (activeAccount?.idTokenClaims?.[`scopes`] as string[]) || []

    // has aad idp
    if (activeAccount?.idTokenClaims?.idp === this.config.bosch_aad_idp_id) {
      // has aad idp and no rcs permissions
      if (
        !tokenPermissions.some(
          (permission) => permission.startsWith('rcs') && permission !== 'rcs.admin',
        )
      ) {
        tokenPermissions = DefaultAadPermissions
        // set default data filter for BoschInternal
        tokenDataFilters.push(WarningDatasources.Rcs)
        tokenDataFilters.push(WarningDatasources.Intermediate)
      }
    }
    // has no aad idp and no permissions
    else if (
      !tokenPermissions.some(
        (permission) => permission.startsWith('rcs') && permission !== 'rcs.admin',
      )
    ) {
      tokenPermissions = [Permissions.RCS_Apps_Rc_Access, Permissions.RCS_RoadSegments_Rc_Read]
    }

    // has aad idp and permissions
    // has no aad idp and permissions

    userPermissions = tokenPermissions
    userDataFilters = tokenDataFilters
    userBackendScopes = tokenBackendScopes

    return { userPermissions, userDataFilters, userBackendScopes }
  }

  public hasPermission(permissions: Permissions[], match: 'all' | 'any' = 'any'): boolean {
    const userPermissions = this.getUserClaims().userPermissions
    if (match === 'all') {
      return permissions.every((permission) => userPermissions.includes(permission))
    } else {
      return permissions.some((permission) => userPermissions.includes(permission))
    }
  }

  public async getAccessToken() {
    if (!this.MsalInitialized) {
      await this.InitializeMsal()
    }

    const authResult = await this.msalService.instance.acquireTokenSilent({
      scopes: [this.config.scope],
    })
    return authResult.accessToken
  }

  public async InitializeMsal() {
    if (this.MsalInitialized) {
      return
    }

    try {
      await this.msalService.instance.initialize()
      this.MsalInitialized = true
      const accounts = this.safeGetAllAccounts()
      if (accounts.length > 0) {
        this.msalService.instance.setActiveAccount(accounts[0])
        this._activeAccount.set(accounts[0])
        this.activeAccountChanges.next(accounts[0])
        const accessTokenRequest = { scopes: [this.config.scope], account: accounts[0] }
        await this.msalService.instance.acquireTokenSilent(accessTokenRequest)
      }
    } catch (e) {
      // if it's not possible to acquire a token we log the user out.    this.logout()  }}
    }
  }
}
